import * as React from 'react';
import { Loader } from 'Src/components/Loader/Loader';
import { NOTIFICATION_TYPE } from 'Src/consts/notifications';
import {
  getSuperAdminSchoolNotifications,
  updateSuperAdminSchoolNotifications,
  SuperAdminSchoolNotificationsPayload
} from 'Src/helpers/service/superadmin/notifications';
import './SuperAdminSchoolNotifications.css';

const STATUS_OPTIONS = [
  { text: 'Auto', value: 'AUTO' },
  { text: 'Manual', value: 'MANUAL' },
  { text: 'Disabled', value: 'DISABLED' }
];

const RECIPIENT_OPTIONS = [
  { text: 'Parents', value: 'PARENT' },
  { text: 'Students', value: 'STUDENT' },
  { text: 'Staff', value: 'STAFF' }
];

const SETTING_SECTION = {
  REGISTRATION_AND_PROFILE_MANAGEMENT: 'REGISTRATION_AND_PROFILE_MANAGEMENT',
  USER_MANAGEMENT: 'USER_MANAGEMENT',
  EVENT_MANAGEMENT: 'EVENT_MANAGEMENT',
  CLUB_MANAGEMENT: 'CLUB_MANAGEMENT',
  CLUB_PROGRAMMES: 'CLUB_PROGRAMMES',
  ATTENDANCE_MANAGEMENT: 'ATTENDANCE_MANAGEMENT',
  AWARD_MANAGEMENT: 'AWARD_MANAGEMENT',
  INVITES_MANAGEMENT: 'INVITES_MANAGEMENT',
  ABSENCE_REQUESTS: 'ABSENCE_REQUESTS',
  OTHER: 'OTHER'
};

const SECTION_LABELS = {
  [SETTING_SECTION.REGISTRATION_AND_PROFILE_MANAGEMENT]: 'Registration and profile management',
  [SETTING_SECTION.USER_MANAGEMENT]: 'User management',
  [SETTING_SECTION.EVENT_MANAGEMENT]: 'Event management',
  [SETTING_SECTION.CLUB_MANAGEMENT]: 'Club management',
  [SETTING_SECTION.CLUB_PROGRAMMES]: 'Activity programmes',
  [SETTING_SECTION.ATTENDANCE_MANAGEMENT]: 'Attendance management',
  [SETTING_SECTION.AWARD_MANAGEMENT]: 'Awards and house points',
  [SETTING_SECTION.INVITES_MANAGEMENT]: 'Invites management',
  [SETTING_SECTION.ABSENCE_REQUESTS]: 'Absence requests',
  [SETTING_SECTION.OTHER]: 'Other'
};

const SECTION_ORDER = [
  SETTING_SECTION.REGISTRATION_AND_PROFILE_MANAGEMENT,
  SETTING_SECTION.USER_MANAGEMENT,
  SETTING_SECTION.EVENT_MANAGEMENT,
  SETTING_SECTION.CLUB_MANAGEMENT,
  SETTING_SECTION.CLUB_PROGRAMMES,
  SETTING_SECTION.ATTENDANCE_MANAGEMENT,
  SETTING_SECTION.AWARD_MANAGEMENT,
  SETTING_SECTION.INVITES_MANAGEMENT,
  SETTING_SECTION.ABSENCE_REQUESTS,
  SETTING_SECTION.OTHER
];

const NOTIFICATION_DEFINITIONS = [
  // Registration and profile management
  {
    type: NOTIFICATION_TYPE.PERMISSION_REQUEST_ACCEPTED,
    section: SETTING_SECTION.REGISTRATION_AND_PROFILE_MANAGEMENT,
    name: 'User role request approved'
  },
  {
    type: NOTIFICATION_TYPE.PERMISSION_REQUEST_REJECTED,
    section: SETTING_SECTION.REGISTRATION_AND_PROFILE_MANAGEMENT,
    name: 'User role request denied'
  },
  {
    type: NOTIFICATION_TYPE.PERMISSION_CREATED,
    section: SETTING_SECTION.REGISTRATION_AND_PROFILE_MANAGEMENT,
    name: 'User granted a new role'
  },
  {
    type: NOTIFICATION_TYPE.PERMISSION_REQUEST_AUTO_ACCEPTED_USER,
    section: SETTING_SECTION.REGISTRATION_AND_PROFILE_MANAGEMENT,
    name: 'User auto-accepted (first admin or parent auto-link)'
  },

  // User management
  {
    type: NOTIFICATION_TYPE.PERMISSION_REQUEST_CREATED,
    section: SETTING_SECTION.USER_MANAGEMENT,
    name: 'New role request received'
  },
  { type: NOTIFICATION_TYPE.MERGE_STUDENT, section: SETTING_SECTION.USER_MANAGEMENT, name: 'Student account merged' },
  { type: NOTIFICATION_TYPE.MERGE_PARENT, section: SETTING_SECTION.USER_MANAGEMENT, name: 'Parent account merged' },
  {
    type: NOTIFICATION_TYPE.STUDENT_ACCOUNT_ACTIVATED,
    section: SETTING_SECTION.USER_MANAGEMENT,
    name: 'Student account activated (set password link)'
  },

  // Event management
  { type: NOTIFICATION_TYPE.EVENT_CREATED, section: SETTING_SECTION.EVENT_MANAGEMENT, name: 'Event created' },
  { type: NOTIFICATION_TYPE.EVENT_UPDATED, section: SETTING_SECTION.EVENT_MANAGEMENT, name: 'Event rescheduled' },
  { type: NOTIFICATION_TYPE.EVENT_CANCELED, section: SETTING_SECTION.EVENT_MANAGEMENT, name: 'Event cancelled' },
  {
    type: NOTIFICATION_TYPE.EVENT_DETAILS_UPDATED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Event details updated'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_DETAILS_STAFF_ADDED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'School user added to event'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_AVAILABILITY_REQUESTED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Availability response requested'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_ADDED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Team player added'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_UPDATED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Team player updated'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_TEAM_PLAYER_REMOVED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Team player removed'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_INDIVIDUAL_PLAYER_ADDED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Individual player added'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_INDIVIDUAL_PLAYER_REMOVED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Individual player removed'
  },
  {
    type: NOTIFICATION_TYPE.MESSAGE_INBOX_CONSENT_REQUEST,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Consent request sent'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_ELIGIBLE_STUDENTS,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Challenge available to join'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_CUSTOM,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Custom event notification'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_FIXTURE_REMINDER,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Fixture reminder'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_REPORT_ADDED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Match report / event notes added'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_INDIVIDUAL_SCORE_ADDED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Results / scores added'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_INDIVIDUAL_SCORE_UPDATED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Results / scores updated'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_TASK_ASSIGNED_ADDED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Task assigned'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_TASK_ASSIGNED_UPDATED,
    section: SETTING_SECTION.EVENT_MANAGEMENT,
    name: 'Assigned task updated'
  },

  // Club management
  { type: NOTIFICATION_TYPE.CLUB_CREATED, section: SETTING_SECTION.CLUB_MANAGEMENT, name: 'Club created' },
  {
    type: NOTIFICATION_TYPE.CLUB_PARTICIPATION_INVITE,
    section: SETTING_SECTION.CLUB_MANAGEMENT,
    name: 'Club invitation received'
  },
  { type: NOTIFICATION_TYPE.CLUB_ACTIVATED, section: SETTING_SECTION.CLUB_MANAGEMENT, name: 'Club activated' },
  {
    type: NOTIFICATION_TYPE.CLUB_ACTIVATED_WAITING_LIST_NOTIFICATION,
    section: SETTING_SECTION.CLUB_MANAGEMENT,
    name: 'Added to club waiting list'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_CLUB_REMINDER,
    section: SETTING_SECTION.CLUB_MANAGEMENT,
    name: 'Club session reminder'
  },

  // Activity programmes
  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENS_SOON,
    section: SETTING_SECTION.CLUB_PROGRAMMES,
    name: 'Activity programme booking opens soon'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENED,
    section: SETTING_SECTION.CLUB_PROGRAMMES,
    name: 'Activity programme booking opened'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_CLOSES_SOON,
    section: SETTING_SECTION.CLUB_PROGRAMMES,
    name: 'Activity programme booking closes soon'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_SUBMITTED,
    section: SETTING_SECTION.CLUB_PROGRAMMES,
    name: 'Activity programme booking submitted'
  },
  {
    type: NOTIFICATION_TYPE.CLUB_PROGRAMME_CLUBS_ACTIVATED,
    section: SETTING_SECTION.CLUB_PROGRAMMES,
    name: 'Club allocations confirmed'
  },

  // Attendance management
  {
    type: NOTIFICATION_TYPE.EVENT_ATTENDANCE_ABSENT,
    section: SETTING_SECTION.ATTENDANCE_MANAGEMENT,
    name: 'Student marked absent'
  },
  {
    type: NOTIFICATION_TYPE.EVENT_ATTENDANCE_PRESENT,
    section: SETTING_SECTION.ATTENDANCE_MANAGEMENT,
    name: 'Student marked present'
  },

  // Awards
  {
    type: NOTIFICATION_TYPE.AWARD_GRANTED,
    section: SETTING_SECTION.AWARD_MANAGEMENT,
    name: 'Award / house points granted'
  },

  // Invites
  {
    type: NOTIFICATION_TYPE.INVITE_INCOMING,
    section: SETTING_SECTION.INVITES_MANAGEMENT,
    name: 'New incoming event invitation'
  },
  {
    type: NOTIFICATION_TYPE.INVITE_ACCEPTED_BY_OPPONENT,
    section: SETTING_SECTION.INVITES_MANAGEMENT,
    name: 'Opponent accepted your event invite'
  },
  {
    type: NOTIFICATION_TYPE.INVITE_REJECTED_BY_OPPONENT,
    section: SETTING_SECTION.INVITES_MANAGEMENT,
    name: 'Opponent rejected your event invite'
  },

  // Absence requests
  {
    type: NOTIFICATION_TYPE.ABSENCE_REQUEST_SENT,
    section: SETTING_SECTION.ABSENCE_REQUESTS,
    name: 'Absence report received'
  },
  {
    type: NOTIFICATION_TYPE.ABSENCE_REQUEST_UPDATED,
    section: SETTING_SECTION.ABSENCE_REQUESTS,
    name: 'Absence report updated'
  },

  // Other (school admin also has CUSTOM but no dedicated section)
  { type: NOTIFICATION_TYPE.CUSTOM, section: SETTING_SECTION.OTHER, name: 'Custom notification' }
];

const SCHEDULE_NOTIFICATION_LINKS = {
  programmeBookingOpensSoonOffsetsMinutes: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_OPENS_SOON,
  programmeBookingClosesSoonOffsetsMinutes: NOTIFICATION_TYPE.CLUB_PROGRAMME_BOOKING_CLOSES_SOON,
  eventFixtureReminderOffsetsMinutes: NOTIFICATION_TYPE.EVENT_FIXTURE_REMINDER,
  eventClubReminderOffsetsMinutes: NOTIFICATION_TYPE.EVENT_CLUB_REMINDER
};

const SCHEDULE_SECTIONS = [
  {
    title: 'Activity programme reminders',
    keys: [
      { key: 'programmeBookingOpensSoonOffsetsMinutes', label: 'Booking opens soon' },
      { key: 'programmeBookingClosesSoonOffsetsMinutes', label: 'Booking closes soon' }
    ]
  },
  {
    title: 'Event reminders',
    keys: [
      { key: 'eventFixtureReminderOffsetsMinutes', label: 'Fixture reminder offsets' },
      { key: 'eventFixtureReminderRecipients', label: 'Fixture reminder recipients' },
      { key: 'eventClubReminderOffsetsMinutes', label: 'Club reminder offsets' },
      { key: 'eventClubReminderRecipients', label: 'Club reminder recipients' }
    ]
  }
];

interface Props {
  schoolId: string;
}

interface ReminderOffsetDraft {
  days: string;
  hours: string;
  minutes: string;
}

interface State {
  isLoading: boolean;
  isSaving: boolean;
  isEditMode: boolean;
  errorMessage: string;
  successMessage: string;
  data: SuperAdminSchoolNotificationsPayload;
  draftNotifications: { [key: string]: string };
  draftSchedule: { [key: string]: any };
  collapsedSections: { [key: string]: boolean };
}

function getDefinition(type: string): any {
  return NOTIFICATION_DEFINITIONS.find(item => item.type === type);
}

function getStatusLabel(status: string): string {
  const option = STATUS_OPTIONS.find(item => item.value === status);

  return option ? option.text : status;
}

function getNotificationLabel(key: string): string {
  const definition = getDefinition(key);

  if (definition) {
    return definition.name;
  }

  return key
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function getNotificationSection(key: string): string {
  const definition = getDefinition(key);

  return definition ? definition.section : SETTING_SECTION.OTHER;
}

function getInitialCollapsedSections(): { [key: string]: boolean } {
  const result = {};

  SECTION_ORDER.forEach(section => {
    result[section] = false;
  });

  return result;
}

function isRecipientKey(key: string): boolean {
  return key === 'eventFixtureReminderRecipients' || key === 'eventClubReminderRecipients';
}

function minutesToOffsetDraft(value: number): ReminderOffsetDraft {
  const totalMinutes = Number(value) || 0;
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes - days * 24 * 60) / 60);
  const minutes = totalMinutes - days * 24 * 60 - hours * 60;

  return {
    days: days > 0 ? String(days) : '',
    hours: hours > 0 ? String(hours) : '',
    minutes: minutes > 0 ? String(minutes) : ''
  };
}

function getKnownScheduleKeys(): string[] {
  return SCHEDULE_SECTIONS.reduce((result, section) => {
    section.keys.forEach(item => result.push(item.key));

    return result;
  }, []);
}

function getDraftSchedule(schedule: any): { [key: string]: any } {
  const result = {};

  getKnownScheduleKeys().forEach(key => {
    result[key] = [];
  });

  Object.keys(schedule || {}).forEach(key => {
    const value = schedule[key];

    if (isRecipientKey(key)) {
      result[key] = Array.isArray(value) ? value.slice() : [];
    } else if (Array.isArray(value)) {
      result[key] = value
        .map(item => minutesToOffsetDraft(Number(item)))
        .filter(item => {
          return item.days !== '' || item.hours !== '' || item.minutes !== '';
        });
    }
  });

  return result;
}

function parsePositiveInteger(value: string): number {
  const parsedValue = parseInt(String(value || '0'), 10);

  if (!Number.isFinite(parsedValue) || parsedValue < 0) {
    return 0;
  }

  return parsedValue;
}

function offsetDraftToMinutes(value: ReminderOffsetDraft): number {
  const days = parsePositiveInteger(value.days);
  const hours = parsePositiveInteger(value.hours);
  const minutes = parsePositiveInteger(value.minutes);

  return days * 24 * 60 + hours * 60 + minutes;
}

function parseOffsetDrafts(value: any): number[] {
  const items = Array.isArray(value) ? value : [];

  return Array.from(
    new Set(items.map(item => offsetDraftToMinutes(item)).filter(item => Number.isFinite(item) && item > 0))
  ).sort((a, b) => b - a);
}

function buildSchedulePayload(draftSchedule: { [key: string]: any }): { [key: string]: any } {
  const result = {};

  Object.keys(draftSchedule || {}).forEach(key => {
    const value = draftSchedule[key];

    if (isRecipientKey(key)) {
      result[key] = Array.isArray(value) ? value.slice() : [];
    } else {
      result[key] = parseOffsetDrafts(value);
    }
  });

  return result;
}

function isNotificationStatusEnabled(status: string): boolean {
  return status === 'AUTO' || status === 'MANUAL';
}

function getLinkedNotificationTypeByScheduleKey(key: string): string {
  return SCHEDULE_NOTIFICATION_LINKS[key] || '';
}

function isScheduleKeyRequired(key: string, draftNotifications: { [key: string]: string }): boolean {
  const notificationType = getLinkedNotificationTypeByScheduleKey(key);

  if (!notificationType) {
    return false;
  }

  return isNotificationStatusEnabled(draftNotifications[notificationType]);
}

function hasScheduleOffsets(value: any): boolean {
  return parseOffsetDrafts(value).length > 0;
}

function formatMinutes(value: number): string {
  if (value % (24 * 60) === 0) {
    const days = value / (24 * 60);
    return `${days} ${days === 1 ? 'day' : 'days'} before`;
  }

  if (value % 60 === 0) {
    const hours = value / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} before`;
  }

  return `${value} minutes before`;
}

function formatScheduleValue(value: any): string {
  if (!Array.isArray(value)) {
    return '';
  }

  if (value.length === 0) {
    return '—';
  }

  return value
    .map(item => {
      if (typeof item === 'number') {
        return formatMinutes(item);
      }

      return String(item);
    })
    .join(', ');
}

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'AUTO':
      return 'badge-success';
    case 'MANUAL':
      return 'badge-warning';
    case 'DISABLED':
      return 'badge-secondary';
    default:
      return 'badge-light';
  }
}

export class SuperAdminSchoolNotifications extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      isSaving: false,
      isEditMode: false,
      errorMessage: '',
      successMessage: '',
      data: {},
      draftNotifications: {},
      draftSchedule: {},
      collapsedSections: getInitialCollapsedSections()
    };
  }

  componentDidMount() {
    this.loadNotifications();
  }

  loadNotifications = (): void => {
    const { schoolId } = this.props;

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    getSuperAdminSchoolNotifications(schoolId)
      .then(data => {
        const notifications = data.notifications || {};

        const schedule = data.notificationSchedule || {};

        this.setState({
          data,
          draftNotifications: { ...notifications },
          draftSchedule: getDraftSchedule(schedule),
          isLoading: false,
          errorMessage: ''
        });
      })
      .catch(error => {
        console.error('Can not load superadmin school notifications', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load notifications. Please check the session and try again.'
        });
      });
  };

  onEditClick = (): void => {
    const notifications = this.state.data.notifications || {};
    const schedule = this.state.data.notificationSchedule || {};

    this.setState({
      isEditMode: true,
      successMessage: '',
      draftNotifications: { ...notifications },
      draftSchedule: getDraftSchedule(schedule)
    });
  };

  onCancelClick = (): void => {
    const notifications = this.state.data.notifications || {};
    const schedule = this.state.data.notificationSchedule || {};

    this.setState({
      isEditMode: false,
      successMessage: '',
      draftNotifications: { ...notifications },
      draftSchedule: getDraftSchedule(schedule)
    });
  };

  onStatusChange = (key: string, event): void => {
    const value = event.target.value;

    this.setState(prevState => ({
      draftNotifications: {
        ...prevState.draftNotifications,
        [key]: value
      },
      successMessage: '',
      errorMessage: ''
    }));
  };

  setAllNotificationStatuses = (status: string): void => {
    this.setState(prevState => {
      const notifications = prevState.data.notifications || {};
      const nextDraftNotifications = { ...prevState.draftNotifications };

      Object.keys(notifications).forEach(key => {
        nextDraftNotifications[key] = status;
      });

      return {
        draftNotifications: nextDraftNotifications,
        successMessage: ''
      };
    });
  };

  setSectionNotificationStatuses = (keys: string[], status: string, event): void => {
    event.stopPropagation();

    this.setState(prevState => {
      const nextDraftNotifications = { ...prevState.draftNotifications };

      keys.forEach(key => {
        nextDraftNotifications[key] = status;
      });

      return {
        draftNotifications: nextDraftNotifications,
        successMessage: ''
      };
    });
  };

  onScheduleOffsetChange = (key: string, index: number, field: string, event): void => {
    const value = event.target.value;

    this.setState(prevState => {
      const currentValue = prevState.draftSchedule[key];
      const currentOffsets: ReminderOffsetDraft[] = Array.isArray(currentValue) ? currentValue.slice() : [];
      const currentOffset = currentOffsets[index] || { days: '', hours: '', minutes: '' };

      currentOffsets[index] = {
        ...currentOffset,
        [field]: value
      };

      return {
        draftSchedule: {
          ...prevState.draftSchedule,
          [key]: currentOffsets
        },
        successMessage: '',
        errorMessage: ''
      };
    });
  };

  onScheduleOffsetAdd = (key: string): void => {
    this.setState(prevState => {
      const currentValue = prevState.draftSchedule[key];
      const currentOffsets: ReminderOffsetDraft[] = Array.isArray(currentValue) ? currentValue.slice() : [];

      return {
        draftSchedule: {
          ...prevState.draftSchedule,
          [key]: [...currentOffsets, { days: '', hours: '', minutes: '' }]
        },
        successMessage: '',
        errorMessage: ''
      };
    });
  };

  onScheduleOffsetRemove = (key: string, index: number): void => {
    this.setState(prevState => {
      const currentValue = prevState.draftSchedule[key];
      const currentOffsets: ReminderOffsetDraft[] = Array.isArray(currentValue) ? currentValue.slice() : [];

      currentOffsets.splice(index, 1);

      return {
        draftSchedule: {
          ...prevState.draftSchedule,
          [key]: currentOffsets
        },
        successMessage: '',
        errorMessage: ''
      };
    });
  };

  onScheduleRecipientChange = (key: string, recipient: string, event): void => {
    const checked = event.target.checked;

    this.setState(prevState => {
      const currentValue = prevState.draftSchedule[key];
      const currentRecipients = Array.isArray(currentValue) ? currentValue : [];
      const nextRecipients: string[] = checked
        ? Array.from(new Set([...currentRecipients, recipient]))
            .map(item => String(item))
            .sort()
        : currentRecipients.filter(item => item !== recipient);

      return {
        draftSchedule: {
          ...prevState.draftSchedule,
          [key]: nextRecipients
        },
        successMessage: ''
      };
    });
  };

  onSectionToggle = (section: string): void => {
    this.setState(prevState => ({
      collapsedSections: {
        ...prevState.collapsedSections,
        [section]: !prevState.collapsedSections[section]
      }
    }));
  };

  getScheduleValidationError(schedulePayload: { [key: string]: any }): string {
    const { draftNotifications } = this.state;
    const invalidLabels: string[] = [];

    Object.keys(SCHEDULE_NOTIFICATION_LINKS).forEach(scheduleKey => {
      const notificationType = getLinkedNotificationTypeByScheduleKey(scheduleKey);
      const status = draftNotifications[notificationType];
      const offsets = schedulePayload[scheduleKey];

      if (isNotificationStatusEnabled(status) && (!Array.isArray(offsets) || offsets.length === 0)) {
        invalidLabels.push(getNotificationLabel(notificationType));
      }
    });

    if (invalidLabels.length === 0) {
      return '';
    }

    return `Please add at least one reminder interval for enabled notification settings: ${invalidLabels.join(', ')}.`;
  }

  onSaveClick = (): void => {
    const { schoolId } = this.props;
    const { draftNotifications, draftSchedule } = this.state;
    const notificationSchedule = buildSchedulePayload(draftSchedule);
    const validationError = this.getScheduleValidationError(notificationSchedule);

    if (validationError) {
      this.setState({ errorMessage: validationError, successMessage: '' });
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    const payload = {
      ...draftNotifications,
      notificationSchedule
    };

    updateSuperAdminSchoolNotifications(schoolId, payload)
      .then(data => {
        const notifications = data.notifications || {};

        const schedule = data.notificationSchedule || {};

        this.setState({
          data,
          draftNotifications: { ...notifications },
          draftSchedule: getDraftSchedule(schedule),
          isSaving: false,
          isEditMode: false,
          successMessage: 'Notifications have been updated.'
        });
      })
      .catch(error => {
        console.error('Can not update superadmin school notifications', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not update notifications. Please check the values and try again.'
        });
      });
  };

  getGroupedNotificationKeys(): { [key: string]: string[] } {
    const notifications = this.state.data.notifications || {};
    const grouped = {};

    SECTION_ORDER.forEach(section => {
      grouped[section] = [];
    });

    Object.keys(notifications)
      .sort((a, b) => getNotificationLabel(a).localeCompare(getNotificationLabel(b)))
      .forEach(key => {
        const section = getNotificationSection(key);
        grouped[section].push(key);
      });

    return grouped;
  }

  renderStatusCell(key: string): React.ReactNode {
    const { data, draftNotifications, isEditMode } = this.state;
    const notifications = data.notifications || {};
    const status = isEditMode ? draftNotifications[key] : notifications[key];

    if (isEditMode) {
      return (
        <select
          className="form-control form-control-sm"
          value={status || 'AUTO'}
          onChange={event => this.onStatusChange(key, event)}
        >
          {STATUS_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      );
    }

    return <span className={`badge ${getStatusBadgeClass(status)}`}>{getStatusLabel(status)}</span>;
  }

  renderNotificationSection(section: string, keys: string[]): React.ReactNode {
    if (keys.length === 0) {
      return null;
    }

    const isCollapsed = this.state.collapsedSections[section];
    const sectionLabel = SECTION_LABELS[section] || section;

    return (
      <div className="card mb-3" key={section}>
        <div
          className="card-header SuperAdminSchoolNotifications__sectionHeader"
          onClick={() => this.onSectionToggle(section)}
        >
          <div>
            <strong>{sectionLabel}</strong>
            <span className="badge badge-light ml-2">{keys.length}</span>
          </div>
          <div className="SuperAdminSchoolNotifications__sectionControls">
            {this.state.isEditMode && (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success mr-2"
                  onClick={event => this.setSectionNotificationStatuses(keys, 'AUTO', event)}
                  disabled={this.state.isSaving}
                >
                  Enable all
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mr-2"
                  onClick={event => this.setSectionNotificationStatuses(keys, 'DISABLED', event)}
                  disabled={this.state.isSaving}
                >
                  Disable all
                </button>
              </>
            )}
            <span className="SuperAdminSchoolNotifications__chevron">{isCollapsed ? '+' : '−'}</span>
          </div>
        </div>

        {!isCollapsed && (
          <div className="card-body p-0">
            <div className="table-responsive mb-0">
              <table className="table table-sm table-striped mb-0">
                <thead>
                  <tr>
                    <th className="notification-type-cell">Notification</th>
                    <th className="notification-status-cell">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map(key => (
                    <tr key={key}>
                      <td>
                        <div>{getNotificationLabel(key)}</div>
                        <div className="notification-key">{key}</div>
                      </td>
                      <td>{this.renderStatusCell(key)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderNotificationsTable(): React.ReactNode {
    const notifications = this.state.data.notifications || {};
    const keys = Object.keys(notifications);

    if (keys.length === 0) {
      return <div className="alert alert-info mb-0">No notification settings found for this school.</div>;
    }

    const grouped = this.getGroupedNotificationKeys();

    return SECTION_ORDER.map(section => this.renderNotificationSection(section, grouped[section]));
  }

  renderScheduleEditor(key: string, value: any): React.ReactNode {
    const { draftNotifications, draftSchedule, isSaving } = this.state;

    if (isRecipientKey(key)) {
      const selectedRecipients = Array.isArray(draftSchedule[key]) ? draftSchedule[key] : [];

      return (
        <div className="SuperAdminSchoolNotifications__recipientControls">
          {RECIPIENT_OPTIONS.map(option => (
            <label className="form-check form-check-inline mb-0" key={option.value}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={selectedRecipients.indexOf(option.value) !== -1}
                onChange={event => this.onScheduleRecipientChange(key, option.value, event)}
                disabled={isSaving}
              />
              <span className="form-check-label">{option.text}</span>
            </label>
          ))}
        </div>
      );
    }

    const offsets: ReminderOffsetDraft[] = Array.isArray(draftSchedule[key]) ? draftSchedule[key] : [];
    const isRequired = isScheduleKeyRequired(key, draftNotifications);
    const isMissingRequiredOffset = isRequired && !hasScheduleOffsets(offsets);

    return (
      <div className={isMissingRequiredOffset ? 'SuperAdminSchoolNotifications__scheduleError' : ''}>
        {offsets.length === 0 && <div className="text-muted small mb-2">No reminder intervals added.</div>}

        {offsets.map((offset, index) => (
          <div className="SuperAdminSchoolNotifications__offsetRow" key={`${key}_${index}`}>
            <div className="SuperAdminSchoolNotifications__offsetField">
              <input
                className="form-control form-control-sm"
                type="number"
                min="0"
                value={offset.days}
                onChange={event => this.onScheduleOffsetChange(key, index, 'days', event)}
                disabled={isSaving}
              />
              <span>days</span>
            </div>
            <div className="SuperAdminSchoolNotifications__offsetField">
              <input
                className="form-control form-control-sm"
                type="number"
                min="0"
                value={offset.hours}
                onChange={event => this.onScheduleOffsetChange(key, index, 'hours', event)}
                disabled={isSaving}
              />
              <span>hours</span>
            </div>
            <div className="SuperAdminSchoolNotifications__offsetField">
              <input
                className="form-control form-control-sm"
                type="number"
                min="0"
                value={offset.minutes}
                onChange={event => this.onScheduleOffsetChange(key, index, 'minutes', event)}
                disabled={isSaving}
              />
              <span>minutes</span>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => this.onScheduleOffsetRemove(key, index)}
              disabled={isSaving}
            >
              Remove
            </button>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => this.onScheduleOffsetAdd(key)}
          disabled={isSaving}
        >
          Add interval
        </button>

        {isMissingRequiredOffset && (
          <div className="text-danger small mt-2">
            At least one interval is required because the linked notification is enabled.
          </div>
        )}
      </div>
    );
  }

  renderScheduleSection(title: string, keys: { key: string; label: string }[]): React.ReactNode {
    const schedule = this.state.isEditMode
      ? this.state.draftSchedule || {}
      : this.state.data.notificationSchedule || {};

    const visibleKeys = this.state.isEditMode ? keys : keys.filter(item => Array.isArray(schedule[item.key]));

    if (visibleKeys.length === 0) {
      return null;
    }

    return (
      <div className="SuperAdminSchoolNotifications__scheduleBlock" key={title}>
        <div className="SuperAdminSchoolNotifications__scheduleTitle">{title}</div>
        <table className="table table-sm mb-0">
          <tbody>
            {visibleKeys.map(item => (
              <tr key={item.key}>
                <td className="notification-schedule-label-cell">
                  <div>{item.label}</div>
                  <div className="notification-key">{item.key}</div>
                </td>
                <td>
                  {this.state.isEditMode
                    ? this.renderScheduleEditor(item.key, schedule[item.key])
                    : formatScheduleValue(schedule[item.key])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  renderSchedule(): React.ReactNode {
    const schedule = this.state.isEditMode
      ? this.state.draftSchedule || {}
      : this.state.data.notificationSchedule || {};
    const knownKeys: string[] = [];
    const blocks = SCHEDULE_SECTIONS.map(section => {
      section.keys.forEach(item => knownKeys.push(item.key));
      return this.renderScheduleSection(section.title, section.keys);
    }).filter(Boolean);

    const otherKeys = Object.keys(schedule).filter(
      key => knownKeys.indexOf(key) === -1 && Array.isArray(schedule[key])
    );

    if (otherKeys.length > 0) {
      blocks.push(
        this.renderScheduleSection(
          'Other reminder settings',
          otherKeys.map(key => ({ key, label: key }))
        )
      );
    }

    if (blocks.length === 0) {
      return <div className="text-muted p-3">No notification schedule settings found.</div>;
    }

    return <>{blocks}</>;
  }

  render() {
    const { errorMessage, isEditMode, isLoading, isSaving, successMessage } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="SuperAdminSchoolNotifications">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Notifications</h4>
            <div className="text-muted">School notification statuses and reminder schedule.</div>
          </div>
          <div>
            {isEditMode ? (
              <>
                <button className="btn btn-outline-secondary mr-2" onClick={this.onCancelClick} disabled={isSaving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={this.onSaveClick} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-outline-secondary mr-2" onClick={this.loadNotifications}>
                  Refresh
                </button>
                <button className="btn btn-primary" onClick={this.onEditClick}>
                  Edit settings
                </button>
              </>
            )}
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && <div className="alert alert-success">{successMessage}</div>}

        <div className="card mb-3">
          <div className="card-header SuperAdminSchoolNotifications__mainHeader">
            <div>
              <strong>Notification statuses</strong>
              <div className="text-muted small">Grouped in the same way as the school admin notification settings.</div>
            </div>
            {isEditMode && (
              <div className="SuperAdminSchoolNotifications__bulkControls">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success mr-2"
                  onClick={() => this.setAllNotificationStatuses('AUTO')}
                  disabled={isSaving}
                >
                  Enable all notifications
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => this.setAllNotificationStatuses('DISABLED')}
                  disabled={isSaving}
                >
                  Disable all notifications
                </button>
              </div>
            )}
          </div>
          <div className="card-body">{this.renderNotificationsTable()}</div>
        </div>

        <div className="card mb-0">
          <div className="card-header">
            <strong>Reminder schedule</strong>
            <div className="text-muted small">
              Edit reminder offsets and recipients together with notification statuses.
            </div>
          </div>
          <div className="card-body p-0">{this.renderSchedule()}</div>
        </div>
      </div>
    );
  }
}
